/**
 * Medication Reconciliation Tab Component
 * Med rec dashboard for RN Command Center
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Mic, Pill, ClipboardCheck, Clipboard, CheckCircle, Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MedRecTab = ({
  isDark,
  medRecSummary,
  medRecPending,
  medRecActive,
  medRecLoading,
  setMedRecLoading,
  setSelectedReconciliation,
  setMedRecPatient,
  setMedRecDialog,
  loadDashboard,
  processVoiceMedRec,
  getAuthHeaders
}) => {
  return (
    <>
      {/* Voice Med Rec Banner */}
      <div className={`mb-4 p-4 rounded-lg border-2 border-dashed ${
        isDark ? "border-orange-700 bg-orange-900/10" : "border-orange-300 bg-orange-50"
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${isDark ? "bg-orange-800" : "bg-orange-200"}`}>
              <Mic className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="font-semibold text-orange-600">Voice-Triggered Med Rec</p>
              <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                Say &ldquo;Start med rec for [patient name]&rdquo; to begin hands-free reconciliation
              </p>
            </div>
          </div>
          <Button 
            className="bg-orange-600 hover:bg-orange-700 flex items-center gap-2"
            onClick={async () => {
              toast.info("Listening... Say 'Start med rec for [patient name]'");
              setTimeout(async () => {
                if (medRecPending.length > 0) {
                  const patient = medRecPending[0];
                  await processVoiceMedRec(`Start med rec for ${patient.patient_name}`, patient.patient_id);
                } else {
                  toast.warning("No patients pending reconciliation");
                }
              }, 1500);
            }}
            data-testid="voice-medrec-btn"
          >
            <Mic className="w-4 h-4" />
            Voice Start
          </Button>
        </div>
        <div className={`mt-3 p-2 rounded ${isDark ? "bg-slate-800" : "bg-white"} text-xs`}>
          <p className="font-medium mb-1">Available Voice Commands:</p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-orange-600">&ldquo;Start med rec&rdquo;</Badge>
            <Badge variant="outline" className="text-orange-600">&ldquo;Med rec for [name]&rdquo;</Badge>
            <Badge variant="outline" className="text-green-600">&ldquo;Accept medication&rdquo;</Badge>
            <Badge variant="outline" className="text-red-600">&ldquo;Reject medication&rdquo;</Badge>
            <Badge variant="outline" className="text-blue-600">&ldquo;Complete reconciliation&rdquo;</Badge>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <Card className={isDark ? "bg-orange-900/20 border-orange-600/30" : "bg-orange-50 border-orange-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-orange-500">{medRecSummary?.pending_count || 0}</p>
            <p className={`text-sm ${isDark ? "text-orange-300" : "text-orange-600"}`}>Pending</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-blue-900/20 border-blue-600/30" : "bg-blue-50 border-blue-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-500">{medRecSummary?.in_progress_count || 0}</p>
            <p className={`text-sm ${isDark ? "text-blue-300" : "text-blue-600"}`}>In Progress</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-red-900/20 border-red-600/30" : "bg-red-50 border-red-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-500">{medRecSummary?.with_discrepancies || 0}</p>
            <p className={`text-sm ${isDark ? "text-red-300" : "text-red-600"}`}>With Discrepancies</p>
          </CardContent>
        </Card>
        <Card className={isDark ? "bg-green-900/20 border-green-600/30" : "bg-green-50 border-green-200"}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-500">
              {medRecActive?.filter(r => r.status === "completed").length || 0}
            </p>
            <p className={`text-sm ${isDark ? "text-green-300" : "text-green-600"}`}>Completed (30d)</p>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        {/* Pending Reconciliations */}
        <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg text-orange-500">
              <Pill className="w-5 h-5" />
              Patients Needing Reconciliation
            </CardTitle>
            <CardDescription>Admissions and transfers requiring medication review</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[350px]">
              {medRecPending.length > 0 ? (
                <div className="space-y-2">
                  {medRecPending.map((patient) => (
                    <div
                      key={patient.patient_id}
                      className={`p-3 rounded-lg border ${
                        patient.urgency === 'high' 
                          ? isDark ? "border-orange-800 bg-orange-900/10" : "border-orange-200 bg-orange-50"
                          : isDark ? "border-slate-600" : "border-gray-200"
                      }`}
                      data-testid={`medrec-pending-${patient.patient_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{patient.patient_name}</p>
                          <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                            {patient.med_count} medications • {patient.reason}
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          className="bg-orange-600 hover:bg-orange-700"
                          onClick={async () => {
                            setMedRecLoading(true);
                            try {
                              const res = await fetch(`${API}/api/rn-voice-command/med-reconciliation/start`, {
                                method: "POST",
                                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                                body: JSON.stringify({ 
                                  patient_id: patient.patient_id, 
                                  source: "admission" 
                                })
                              });
                              if (res.ok) {
                                const data = await res.json();
                                setSelectedReconciliation(data);
                                setMedRecPatient(patient);
                                setMedRecDialog(true);
                                loadDashboard();
                                toast.success("Reconciliation started");
                              }
                            } catch (error) {
                              // Demo mode
                              setSelectedReconciliation({
                                reconciliation_id: `demo_${Date.now()}`,
                                patient_name: patient.patient_name,
                                current_medications: [
                                  { name: "Morphine SR", dose: "15mg", frequency: "Q12H", route: "PO" },
                                  { name: "Lorazepam", dose: "0.5mg", frequency: "Q8H PRN", route: "PO" },
                                  { name: "Haloperidol", dose: "1mg", frequency: "Q6H PRN", route: "PO" }
                                ],
                                discrepancies: [
                                  { id: "d1", type: "dose_discrepancy", medication_name: "Morphine SR", severity: "high", description: "Dose mismatch: current 15mg vs hospital 30mg", resolved: false },
                                  { id: "d2", type: "missing_from_current", medication: { name: "Gabapentin", dose: "300mg" }, severity: "high", description: "Gabapentin found in hospital records but not in current list", resolved: false }
                                ],
                                discrepancies_count: 2
                              });
                              setMedRecPatient(patient);
                              setMedRecDialog(true);
                              toast.success("Reconciliation started (demo mode)");
                            } finally {
                              setMedRecLoading(false);
                            }
                          }}
                          disabled={medRecLoading}
                        >
                          {medRecLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`text-center py-8 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                  <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                  <p>All patients reconciled</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Active Reconciliations */}
        <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg text-blue-500">
              <ClipboardCheck className="w-5 h-5" />
              In Progress Reconciliations
            </CardTitle>
            <CardDescription>Active reconciliation sessions with discrepancies</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[350px]">
              {medRecActive.length > 0 ? (
                <div className="space-y-2">
                  {medRecActive.map((recon) => (
                    <div
                      key={recon.id}
                      className={`p-3 rounded-lg border ${
                        recon.discrepancies_count > 0
                          ? isDark ? "border-red-800 bg-red-900/10" : "border-red-200 bg-red-50"
                          : isDark ? "border-slate-600" : "border-gray-200"
                      }`}
                      data-testid={`medrec-active-${recon.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{recon.patient_name}</p>
                            <Badge className={recon.status === "in_progress" ? "bg-blue-500" : "bg-green-500"}>
                              {recon.status}
                            </Badge>
                          </div>
                          <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                            {recon.discrepancies_count > 0 
                              ? `${recon.discrepancies_count} discrepancies to resolve`
                              : "No discrepancies found"}
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          variant={recon.discrepancies_count > 0 ? "default" : "outline"}
                          className={recon.discrepancies_count > 0 ? "bg-red-600 hover:bg-red-700" : ""}
                          onClick={async () => {
                            try {
                              const res = await fetch(`${API}/api/rn-voice-command/med-reconciliation/${recon.id}`, {
                                headers: getAuthHeaders()
                              });
                              if (res.ok) {
                                const data = await res.json();
                                setSelectedReconciliation(data.reconciliation);
                                setMedRecPatient({ patient_name: recon.patient_name });
                                setMedRecDialog(true);
                              }
                            } catch (error) {
                              toast.error("Failed to load reconciliation");
                            }
                          }}
                        >
                          {recon.discrepancies_count > 0 ? "Resolve" : "View"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`text-center py-8 ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                  <Clipboard className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No active reconciliations</p>
                  <p className="text-xs mt-1">Start a new reconciliation from the pending list</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
      
      {/* CMS Med Rec Requirements Info */}
      <Card className={`mt-4 ${isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">CMS Medication Reconciliation Requirements</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4 text-center">
            <div className={`p-3 rounded-lg ${isDark ? "bg-slate-700" : "bg-gray-50"}`}>
              <p className="font-semibold text-orange-500">Admission</p>
              <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>Within 48 hours</p>
            </div>
            <div className={`p-3 rounded-lg ${isDark ? "bg-slate-700" : "bg-gray-50"}`}>
              <p className="font-semibold text-blue-500">Transfer</p>
              <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>At each transition</p>
            </div>
            <div className={`p-3 rounded-lg ${isDark ? "bg-slate-700" : "bg-gray-50"}`}>
              <p className="font-semibold text-purple-500">Hospital Return</p>
              <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>Within 24 hours</p>
            </div>
            <div className={`p-3 rounded-lg ${isDark ? "bg-slate-700" : "bg-gray-50"}`}>
              <p className="font-semibold text-teal-500">Discharge</p>
              <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>At time of discharge</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default MedRecTab;
